import { useCallback, useEffect, useRef, useState } from "react";

import {
  deleteCropArea,
  deleteFarmMapLine,
  fetchFarmCropAreas,
  fetchFarmMapLines,
  saveCropArea,
  saveFarmMapLine,
  updateCropAreaGeometry,
  updateCropAreaName,
} from "../../utils/farmMapDrawingApi";
import { createGroupFarm, updateGroupFarmBoundary } from "../PendingSchedules/serviceOrderLocationApi";
import {
  buildPlotName,
  closePolygonRing,
  buildPolygonShapeMetrics,
  computePointsLengthMeters,
  computeRingCentroid,
  formatDistanceM,
  getLandPlotMapStyle,
  resolveExistingPlotName,
  stripPlotAreaSuffix,
  getPlotDisplayLabel,
  getPlotMapLabel,
  isGroupSubFarm,
  landPlotsInsideRing,
  normalizePolygonRings,
  parseBoundaryGeoJsonRing,
  parseLandplotPolygon,
  parseLandplotRing,
  ringToEditablePoints,
  pathToLineWkt,
  validateHolesInsideOuter,
} from "../../utils/mapMeasurements";

const TOOLS = {
  LINE: "line",
  POLYGON: "polygon",
  FARM: "farm",
};

export function useFarmMapDrawingTools({
  mapRef,
  mapsRef,
  farmId,
  mapReady,
  highlightedCropIds,
  onCropClick,
  allLandPlots = [],
  groupFarms = [],
  highlightedSubFarmId = null,
  highlightedSubFarmIds = [],
  onSubFarmClick,
  onUnpublishedChange,
  onHoleFinished,
  onHoleFinishFailed,
}) {
  const [activeTool, setActiveTool] = useState(null);
  const [measureOnly, setMeasureOnly] = useState(false);
  const [pendingShape, setPendingShape] = useState(null);
  const [isAddingHole, setIsAddingHole] = useState(false);
  const [cropAreas, setCropAreas] = useState([]);
  const [savedLines, setSavedLines] = useState([]);
  const [loadingDrawings, setLoadingDrawings] = useState(true);
  const [savingDrawing, setSavingDrawing] = useState(false);
  const [draftPointCount, setDraftPointCount] = useState(0);
  const [draftLineDistanceM, setDraftLineDistanceM] = useState(0);
  const [draftLastSegmentM, setDraftLastSegmentM] = useState(0);
  const [editingCropId, setEditingCropId] = useState(null);
  const [editingSubFarmId, setEditingSubFarmId] = useState(null);

  const staticOverlaysRef = useRef([]);
  const draftOverlaysRef = useRef([]);
  const pendingOverlaysRef = useRef([]);
  const draftPointsRef = useRef([]);
  const mapClickListenerRef = useRef(null);
  const pendingShapeRef = useRef(null);
  const isAddingHoleRef = useRef(false);

  const assignPendingShape = useCallback((value) => {
    if (typeof value === "function") {
      setPendingShape((prev) => {
        const next = value(prev);
        pendingShapeRef.current = next;
        return next;
      });
      return;
    }
    pendingShapeRef.current = value;
    setPendingShape(value);
  }, []);

  useEffect(() => {
    pendingShapeRef.current = pendingShape;
  }, [pendingShape]);

  useEffect(() => {
    isAddingHoleRef.current = isAddingHole;
  }, [isAddingHole]);

  const clearStaticOverlays = useCallback(() => {
    const maps = mapsRef.current;
    staticOverlaysRef.current.forEach((overlay) => {
      if (!overlay) return;
      if (overlay.setMap) {
        overlay.setMap(null);
        return;
      }
      if (maps && maps.event && maps.event.removeListener) {
        maps.event.removeListener(overlay);
      }
    });
    staticOverlaysRef.current = [];
  }, [mapsRef]);

  const clearDraftOverlays = useCallback(() => {
    const maps = mapsRef.current;
    draftOverlaysRef.current.forEach((overlay) => {
      if (!overlay) return;
      if (overlay.setMap) {
        overlay.setMap(null);
        return;
      }
      if (maps && maps.event && maps.event.removeListener) {
        maps.event.removeListener(overlay);
      }
    });
    draftOverlaysRef.current = [];
  }, [mapsRef]);

  const syncDraftLineMetrics = useCallback(
    (points) => {
      const maps = mapsRef.current;
      if (!points || points.length < 2) {
        setDraftLineDistanceM(0);
        setDraftLastSegmentM(0);
        return;
      }
      setDraftLineDistanceM(computePointsLengthMeters(points, maps));
      setDraftLastSegmentM(
        computePointsLengthMeters(points.slice(-2), maps)
      );
    },
    [mapsRef]
  );

  const clearDraftPoints = useCallback(() => {
    draftPointsRef.current = [];
    setDraftPointCount(0);
    syncDraftLineMetrics([]);
    clearDraftOverlays();
  }, [clearDraftOverlays, syncDraftLineMetrics]);

  const clearPendingOverlays = useCallback(() => {
    const maps = mapsRef.current;
    pendingOverlaysRef.current.forEach((overlay) => {
      if (!overlay) return;
      if (overlay.setMap) {
        overlay.setMap(null);
        return;
      }
      if (maps && maps.event && maps.event.removeListener) {
        maps.event.removeListener(overlay);
      }
    });
    pendingOverlaysRef.current = [];
  }, [mapsRef]);

  const clearDraft = useCallback(() => {
    draftPointsRef.current = [];
    setDraftPointCount(0);
    clearDraftOverlays();
  }, [clearDraftOverlays]);

  const detachMapClickListener = useCallback(() => {
    const maps = mapsRef.current;
    if (maps && maps.event && mapClickListenerRef.current) {
      maps.event.removeListener(mapClickListenerRef.current);
    }
    mapClickListenerRef.current = null;
  }, [mapsRef]);

  const clearPendingOverlay = useCallback(() => {
    clearDraftOverlays();
    clearPendingOverlays();
    draftPointsRef.current = [];
    setDraftPointCount(0);
    assignPendingShape(null);
    setIsAddingHole(false);
  }, [assignPendingShape, clearDraftOverlays, clearPendingOverlays]);

  const loadDrawings = useCallback(async () => {
    if (!farmId) return;
    setLoadingDrawings(true);
    try {
      const [crops, lines] = await Promise.all([
        fetchFarmCropAreas(farmId),
        fetchFarmMapLines(farmId),
      ]);
      setCropAreas(crops);
      setSavedLines(lines);
    } catch (error) {
      setCropAreas([]);
      setSavedLines([]);
    } finally {
      setLoadingDrawings(false);
    }
  }, [farmId]);

  useEffect(() => {
    loadDrawings();
  }, [loadDrawings]);

  const renderStaticOverlays = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;
    clearStaticOverlays();

    cropAreas.forEach((crop, index) => {
      if (editingCropId && String(crop.id) === String(editingCropId)) {
        return;
      }
      if (
        pendingShape &&
        pendingShape.editCropId &&
        String(crop.id) === String(pendingShape.editCropId)
      ) {
        return;
      }
      const { outer, holes } = parseLandplotPolygon(crop);
      const ring = ringToEditablePoints(outer);
      if (ring.length < 3) return;

      const normalized = normalizePolygonRings(ring, holes);
      const polygonPaths =
        normalized.holes.length > 0
          ? [normalized.outer, ...normalized.holes]
          : normalized.outer;

      const label = getPlotDisplayLabel(crop).trim() || getPlotMapLabel(crop).trim();
      const isHighlighted = highlightedCropIds.includes(crop.id);
      const plotStyle = getLandPlotMapStyle(isHighlighted);
      const canSelectFromMap =
        Boolean(onCropClick) &&
        !activeTool &&
        !pendingShape &&
        !editingCropId &&
        !editingSubFarmId;

      const polygon = new maps.Polygon({
        paths: polygonPaths,
        map,
        strokeColor: plotStyle.strokeColor,
        strokeOpacity: plotStyle.strokeOpacity,
        strokeWeight: plotStyle.strokeWeight,
        fillColor: plotStyle.fillColor,
        fillOpacity: plotStyle.fillOpacity,
        zIndex: isHighlighted ? 6 : 5,
        clickable: canSelectFromMap,
      });
      staticOverlaysRef.current.push(polygon);

      normalized.holes.forEach((hole) => {
        if (!hole || hole.length < 3) return;
        staticOverlaysRef.current.push(
          new maps.Polyline({
            path: closePolygonRing(hole),
            map,
            strokeColor: "#E53935",
            strokeOpacity: 1,
            strokeWeight: 3,
            zIndex: isHighlighted ? 5 : 4,
            clickable: false,
          })
        );
      });

      if (canSelectFromMap) {
        staticOverlaysRef.current.push(
          maps.event.addListener(polygon, "click", (event) => {
            if (event && event.stop) {
              event.stop();
            }
            onCropClick(crop.id);
          })
        );
      }

      if (crop.landplot_center && crop.landplot_center.lat != null) {
        const marker = new maps.Marker({
          map,
          position: crop.landplot_center,
          label: {
            text: label,
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: "11px",
          },
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: canSelectFromMap ? 14 : 0,
            fillOpacity: 0,
            strokeOpacity: 0,
          },
          zIndex: isHighlighted ? 8 : 7,
          clickable: canSelectFromMap,
          title: label,
        });
        staticOverlaysRef.current.push(marker);

        if (canSelectFromMap) {
          staticOverlaysRef.current.push(
            maps.event.addListener(marker, "click", (event) => {
              if (event && event.stop) {
                event.stop();
              }
              onCropClick(crop.id);
            })
          );
        }
      }
    });

    savedLines.forEach((line) => {
      const ring = Array.isArray(line.ring) ? line.ring : [];
      if (ring.length < 2) return;

      staticOverlaysRef.current.push(
        new maps.Polyline({
          path: ring,
          map,
          strokeColor: "#00E676",
          strokeOpacity: 0.95,
          strokeWeight: 4,
          zIndex: 2,
        })
      );
    });

    (groupFarms || []).forEach((farm) => {
      if (!isGroupSubFarm(farm)) return;
      if (
        editingSubFarmId != null &&
        String(farm.id) === String(editingSubFarmId)
      ) {
        return;
      }
      const ring = parseBoundaryGeoJsonRing(farm.boundary_geojson);
      if (ring.length < 3) return;

      const highlightedIds = []
        .concat(highlightedSubFarmIds || [])
        .concat(highlightedSubFarmId != null ? [highlightedSubFarmId] : [])
        .map(String);
      const isSubFarmHighlighted = highlightedIds.includes(String(farm.id));
      const canSelectSubFarmFromMap =
        Boolean(onSubFarmClick) &&
        !activeTool &&
        !pendingShape &&
        !editingCropId &&
        !editingSubFarmId;

      const polygon = new maps.Polygon({
        paths: ring,
        map,
        strokeColor: isSubFarmHighlighted ? "#FFD600" : "#1565C0",
        strokeOpacity: 1,
        strokeWeight: isSubFarmHighlighted ? 4 : 3,
        fillColor: isSubFarmHighlighted ? "#A5D6A7" : "#1565C0",
        fillOpacity: isSubFarmHighlighted ? 0.32 : 0,
        zIndex: isSubFarmHighlighted ? 4 : 2,
        clickable: false,
      });
      staticOverlaysRef.current.push(polygon);

      if (canSelectSubFarmFromMap) {
        staticOverlaysRef.current.push(
          new maps.Polyline({
            path: closePolygonRing(ring),
            map,
            strokeColor: isSubFarmHighlighted ? "#FFD600" : "#1565C0",
            strokeOpacity: isSubFarmHighlighted ? 1 : 0.85,
            strokeWeight: isSubFarmHighlighted ? 4 : 3,
            zIndex: isSubFarmHighlighted ? 3 : 2,
            clickable: true,
          })
        );
        const boundaryLine =
          staticOverlaysRef.current[staticOverlaysRef.current.length - 1];
        staticOverlaysRef.current.push(
          maps.event.addListener(boundaryLine, "click", (event) => {
            if (event && event.stop) {
              event.stop();
            }
            onSubFarmClick(farm.id);
          })
        );
      }

      const centroid = computeRingCentroid(ring);
      const farmLabel = (farm.nome_da_fazenda || "").trim();
      if (centroid && farmLabel) {
        const marker = new maps.Marker({
          map,
          position: centroid,
          label: {
            text: farmLabel,
            color: isSubFarmHighlighted ? "#333333" : "#1565C0",
            fontWeight: "bold",
            fontSize: "12px",
          },
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 0,
            fillOpacity: 0,
            strokeOpacity: 0,
          },
          zIndex: isSubFarmHighlighted ? 9 : 8,
          clickable: canSelectSubFarmFromMap,
          title: farmLabel,
        });
        staticOverlaysRef.current.push(marker);

        if (canSelectSubFarmFromMap) {
          staticOverlaysRef.current.push(
            maps.event.addListener(marker, "click", (event) => {
              if (event && event.stop) {
                event.stop();
              }
              onSubFarmClick(farm.id);
            })
          );
        }
      }
    });
  }, [
    mapRef,
    mapsRef,
    cropAreas,
    savedLines,
    groupFarms,
    highlightedSubFarmId,
    highlightedSubFarmIds,
    highlightedCropIds,
    activeTool,
    pendingShape,
    editingCropId,
    editingSubFarmId,
    onCropClick,
    onSubFarmClick,
    clearStaticOverlays,
  ]);

  useEffect(() => {
    if (!mapReady) return;
    renderStaticOverlays();
  }, [mapReady, renderStaticOverlays]);

  const renderPendingOverlays = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    clearPendingOverlays();
    if (!map || !maps || !pendingShape) return;

    const isFarm = pendingShape.type === TOOLS.FARM;
    const outerPoints = pendingShape.points || [];
    const holes = pendingShape.holes || [];
    const normalized = normalizePolygonRings(outerPoints, holes);

    if (normalized.outer.length >= 3) {
      const polygonPaths =
        normalized.holes.length > 0
          ? [normalized.outer, ...normalized.holes]
          : normalized.outer;

      pendingOverlaysRef.current.push(
        new maps.Polygon({
          paths: polygonPaths,
          map,
          strokeColor: isFarm ? "#1565C0" : "#FFD600",
          strokeOpacity: 1,
          strokeWeight: isFarm ? 3 : 2,
          fillColor: isFarm ? "#1565C0" : "#FFD600",
          fillOpacity: isFarm ? 0 : 0.12,
          zIndex: 6,
          clickable: false,
        })
      );

      holes.forEach((hole) => {
        if (!hole || hole.length < 3) return;
        const holeRing = normalizePolygonRings(normalized.outer, [hole]).holes[0];
        if (!holeRing || holeRing.length < 3) return;
        pendingOverlaysRef.current.push(
          new maps.Polygon({
            paths: holeRing,
            map,
            strokeColor: "#E53935",
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: "#E53935",
            fillOpacity: 0.18,
            zIndex: 7,
            clickable: false,
          })
        );
      });

      if (!isFarm) {
        const centroid = computeRingCentroid(normalized.outer);
        const previewLabel = buildPlotName(
          pendingShape.cropName || "",
          pendingShape.areaHa
        );
        if (centroid && previewLabel) {
          pendingOverlaysRef.current.push(
            new maps.Marker({
              map,
              position: centroid,
              label: {
                text: previewLabel,
                color: "#ffffff",
                fontWeight: "bold",
                fontSize: "11px",
              },
              icon: {
                path: maps.SymbolPath.CIRCLE,
                scale: 14,
                fillOpacity: 0,
                strokeOpacity: 0,
              },
              zIndex: 8,
              clickable: false,
            })
          );
        }
      }

      if (isFarm) {
        const insidePlots = landPlotsInsideRing(allLandPlots, outerPoints);
        insidePlots.forEach((plot) => {
          const ring = parseLandplotRing(plot);
          if (ring.length < 3) return;
          const plotStyle = getLandPlotMapStyle(true);
          pendingOverlaysRef.current.push(
            new maps.Polygon({
              paths: ring,
              map,
              strokeColor: plotStyle.strokeColor,
              strokeOpacity: plotStyle.strokeOpacity,
              strokeWeight: plotStyle.strokeWeight,
              fillColor: plotStyle.fillColor,
              fillOpacity: 0.35,
              zIndex: 5,
              clickable: false,
            })
          );
        });
      }
    }
  }, [
    mapRef,
    mapsRef,
    pendingShape,
    allLandPlots,
    clearPendingOverlays,
  ]);

  useEffect(() => {
    if (!mapReady) return;
    renderPendingOverlays();
  }, [mapReady, renderPendingOverlays, isAddingHole, draftPointCount]);

  const updateDraftPreview = useCallback(
    (toolOverride) => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    const points = draftPointsRef.current;
    const tool = toolOverride || activeTool;

    clearDraftOverlays();
    if (!map || !maps || points.length === 0) return;
    if (!tool && !isAddingHole) return;

    const draftTool = isAddingHole ? TOOLS.POLYGON : tool;
    const markerColor = isAddingHole
      ? "#E53935"
      : draftTool === TOOLS.LINE
        ? "#00E676"
        : draftTool === TOOLS.FARM
          ? "#1565C0"
          : "#FFD600";

    points.forEach((point, index) => {
      const marker = new maps.Marker({
        position: point,
        map,
        draggable: true,
        label: {
          text: String(index + 1),
          color: "#ffffff",
          fontWeight: "bold",
        },
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: markerColor,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
        zIndex: 5,
      });
      draftOverlaysRef.current.push(marker);
      draftOverlaysRef.current.push(
        maps.event.addListener(marker, "dragend", (event) => {
          draftPointsRef.current[index] = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };
          updateDraftPreview();
        })
      );
    });

    if (points.length >= 2) {
      if (draftTool === TOOLS.LINE) {
        draftOverlaysRef.current.push(
          new maps.Polyline({
            path: points,
            map,
            strokeColor: "#00E676",
            strokeOpacity: 0.95,
            strokeWeight: 4,
            zIndex: 4,
            clickable: false,
          })
        );

        if (measureOnly) {
          const totalM = computePointsLengthMeters(points, maps);
          const lastPoint = points[points.length - 1];
          draftOverlaysRef.current.push(
            new maps.Marker({
              map,
              position: lastPoint,
              label: {
                text: formatDistanceM(totalM),
                color: "#ffffff",
                fontWeight: "bold",
                fontSize: "11px",
              },
              icon: {
                path: maps.SymbolPath.CIRCLE,
                scale: 16,
                fillColor: "#212121",
                fillOpacity: 0.88,
                strokeColor: "#ffffff",
                strokeWeight: 2,
              },
              zIndex: 6,
              clickable: false,
            })
          );
        }
      } else {
        draftOverlaysRef.current.push(
          new maps.Polygon({
            paths: points,
            map,
            strokeColor: isAddingHole
              ? "#E53935"
              : draftTool === TOOLS.FARM
                ? "#1565C0"
                : "#FFD600",
            strokeOpacity: 1,
            strokeWeight: draftTool === TOOLS.FARM ? 3 : 2,
            fillColor: isAddingHole
              ? "#E53935"
              : draftTool === TOOLS.FARM
                ? "#1565C0"
                : "#FFD600",
            fillOpacity: isAddingHole ? 0.18 : draftTool === TOOLS.FARM ? 0 : 0.2,
            zIndex: 4,
            clickable: false,
          })
        );
      }
    }

    syncDraftLineMetrics(points);
  },
    [activeTool, isAddingHole, measureOnly, mapRef, mapsRef, clearDraftOverlays, syncDraftLineMetrics]
  );

  const handleMapClick = useCallback(
    (event) => {
      if (!activeTool && !isAddingHoleRef.current) return;
      if (pendingShapeRef.current && !isAddingHoleRef.current) return;

      const point = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      draftPointsRef.current = draftPointsRef.current.concat(point);
      setDraftPointCount(draftPointsRef.current.length);
      updateDraftPreview();
    },
    [activeTool, updateDraftPreview]
  );

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    const drawingActive =
      mapReady && map && maps && (activeTool || isAddingHole) && (!pendingShape || isAddingHole);

    if (!drawingActive) {
      detachMapClickListener();
      return undefined;
    }

    map.setOptions({ draggableCursor: "crosshair", draggingCursor: "crosshair" });
    mapClickListenerRef.current = maps.event.addListener(map, "click", handleMapClick);

    return () => {
      detachMapClickListener();
      map.setOptions({ draggableCursor: null, draggingCursor: null });
    };
  }, [
    activeTool,
    isAddingHole,
    pendingShape,
    mapReady,
    mapRef,
    mapsRef,
    handleMapClick,
    detachMapClickListener,
  ]);

  const finishDraftDrawing = useCallback(() => {
    const maps = mapsRef.current;
    const points = draftPointsRef.current.slice();
    const currentPendingShape = pendingShapeRef.current;

    if (isAddingHoleRef.current && currentPendingShape && currentPendingShape.points) {
      if (points.length < 3) {
        if (onHoleFinishFailed) {
          onHoleFinishFailed();
        }
        return;
      }
      let finishedHoleCount = 0;
      assignPendingShape((prev) => {
        if (!prev || !prev.points) return prev;
        const nextHoles = (prev.holes || []).concat([points]);
        finishedHoleCount = nextHoles.length;
        const metrics = buildPolygonShapeMetrics(prev.points, nextHoles, maps);
        return {
          ...prev,
          holes: nextHoles,
          ...metrics,
          plotsInside:
            prev.type === TOOLS.FARM
              ? landPlotsInsideRing(allLandPlots, prev.points)
              : prev.plotsInside,
        };
      });
      draftPointsRef.current = [];
      setDraftPointCount(0);
      setIsAddingHole(false);
      clearDraftOverlays();
      detachMapClickListener();
      if (onHoleFinished && finishedHoleCount > 0) {
        onHoleFinished(finishedHoleCount);
      }
      return;
    }

    if (!activeTool) return;

    const minPoints = activeTool === TOOLS.LINE ? 2 : 3;
    if (points.length < minPoints) return;

    const editingCrop = editingCropId
      ? cropAreas.find((item) => String(item.id) === String(editingCropId)) ||
        allLandPlots.find((item) => String(item.id) === String(editingCropId))
      : null;
    const existingHoles = editingCrop
      ? parseLandplotPolygon(editingCrop).holes.map((hole) =>
          ringToEditablePoints(hole)
        )
      : [];

    if (activeTool === TOOLS.LINE) {
      if (measureOnly) {
        return;
      }
      assignPendingShape({
        type: TOOLS.LINE,
        points,
        distanceM: computePointsLengthMeters(points, maps),
        pathWkt: pathToLineWkt(points),
      });
    } else if (activeTool === TOOLS.FARM) {
      const metrics = buildPolygonShapeMetrics(points, [], maps);
      const plotsInside = landPlotsInsideRing(allLandPlots, points);
      const editingSubFarm = editingSubFarmId
        ? (groupFarms || []).find(
            (item) => String(item.id) === String(editingSubFarmId)
          )
        : null;
      assignPendingShape({
        type: TOOLS.FARM,
        points,
        holes: [],
        plotsInside,
        assignedPlotCount: plotsInside.length,
        editSubFarmId: editingSubFarm ? editingSubFarm.id : null,
        subFarmName: editingSubFarm
          ? (editingSubFarm.nome_da_fazenda || "").trim()
          : "",
        ...metrics,
      });
    } else {
      const metrics = buildPolygonShapeMetrics(points, existingHoles, maps);
      assignPendingShape({
        type: TOOLS.POLYGON,
        points,
        holes: existingHoles,
        ...metrics,
        editCropId: editingCrop ? editingCrop.id : null,
        cropName: editingCrop ? resolveExistingPlotName(editingCrop) : "",
      });
    }

    draftPointsRef.current = [];
    setDraftPointCount(0);
    setEditingCropId(null);
    setEditingSubFarmId(null);
    setActiveTool(null);
    setMeasureOnly(false);
    setIsAddingHole(false);
    detachMapClickListener();
  }, [
    activeTool,
    mapsRef,
    assignPendingShape,
    detachMapClickListener,
    editingCropId,
    editingSubFarmId,
    groupFarms,
    cropAreas,
    allLandPlots,
    clearDraftOverlays,
    measureOnly,
    onHoleFinished,
    onHoleFinishFailed,
  ]);

  const undoDraftPoint = useCallback(() => {
    if (draftPointsRef.current.length === 0) return;
    draftPointsRef.current = draftPointsRef.current.slice(0, -1);
    setDraftPointCount(draftPointsRef.current.length);
    updateDraftPreview();
  }, [updateDraftPreview]);

  const startAddingHole = useCallback(() => {
    const shape = pendingShapeRef.current;
    if (!shape || shape.type === TOOLS.LINE) return;
    draftPointsRef.current = [];
    setDraftPointCount(0);
    setIsAddingHole(true);
    clearDraftOverlays();
  }, [clearDraftOverlays]);

  const buildPendingCropShape = useCallback(
    (crop) => {
      const maps = mapsRef.current;
      const { outer, holes } = parseLandplotPolygon(crop);
      const points = ringToEditablePoints(outer);
      if (points.length < 3) return null;
      const editableHoles = holes.map((hole) => ringToEditablePoints(hole));
      const metrics = buildPolygonShapeMetrics(points, editableHoles, maps);
      return {
        type: TOOLS.POLYGON,
        points,
        holes: editableHoles,
        ...metrics,
        editCropId: crop.id,
        cropName: resolveExistingPlotName(crop),
      };
    },
    [mapsRef]
  );

  const startAddingHoleToCrop = useCallback(
    (cropId) => {
      if (!mapReady) return;
      const crop = cropAreas.find((item) => String(item.id) === String(cropId));
      if (!crop) return;

      const nextShape = buildPendingCropShape(crop);
      if (!nextShape) return;

      clearPendingOverlay();
      assignPendingShape(nextShape);
      setEditingCropId(null);
      setActiveTool(null);
      setMeasureOnly(false);
      draftPointsRef.current = [];
      setDraftPointCount(0);
      setIsAddingHole(true);
      clearDraftOverlays();
    },
    [
      mapReady,
      cropAreas,
      buildPendingCropShape,
      clearPendingOverlay,
      assignPendingShape,
      clearDraftOverlays,
    ]
  );

  const cancelAddingHole = useCallback(() => {
    setIsAddingHole(false);
    draftPointsRef.current = [];
    setDraftPointCount(0);
    clearDraftOverlays();
  }, [clearDraftOverlays]);

  const removeHoleAt = useCallback(
    (holeIndex) => {
      assignPendingShape((prev) => {
        if (!prev || prev.type === TOOLS.LINE) return prev;
        const maps = mapsRef.current;
        const holes = (prev.holes || []).filter(
          (_, index) => index !== holeIndex
        );
        const metrics = buildPolygonShapeMetrics(prev.points, holes, maps);
        return {
          ...prev,
          holes,
          ...metrics,
        };
      });
    },
    [assignPendingShape, mapsRef]
  );

  const startTool = useCallback(
    (tool, onlyMeasure = false) => {
      if (!mapReady || editingCropId || editingSubFarmId) return;
      if (tool === TOOLS.FARM && onlyMeasure) return;
      clearPendingOverlay();
      setMeasureOnly(onlyMeasure);
      setActiveTool(tool);
    },
    [clearPendingOverlay, mapReady, editingCropId, editingSubFarmId]
  );

  const closeTool = useCallback(() => {
    clearPendingOverlay();
    clearDraftPoints();
    setEditingCropId(null);
    setEditingSubFarmId(null);
    setActiveTool(null);
    setMeasureOnly(false);
    setIsAddingHole(false);
    detachMapClickListener();
  }, [clearPendingOverlay, clearDraftPoints, detachMapClickListener]);

  const startEditingSubFarm = useCallback(
    (subFarmId) => {
      if (!mapReady) return;
      const farm = (groupFarms || []).find(
        (item) => String(item.id) === String(subFarmId)
      );
      if (!farm || !farm.boundary_geojson) return;

      const ring = parseBoundaryGeoJsonRing(farm.boundary_geojson);
      const points = ringToEditablePoints(ring);
      if (points.length < 3) return;

      clearPendingOverlay();
      draftPointsRef.current = points;
      setDraftPointCount(points.length);
      setEditingSubFarmId(farm.id);
      setMeasureOnly(false);
      setActiveTool(TOOLS.FARM);
      updateDraftPreview(TOOLS.FARM);
    },
    [mapReady, groupFarms, clearPendingOverlay, updateDraftPreview]
  );

  const startEditingCrop = useCallback(
    (cropId) => {
      if (!mapReady) return;
      const crop = cropAreas.find((item) => String(item.id) === String(cropId));
      if (!crop) return;

      const ring = parseLandplotRing(crop);
      const points = ringToEditablePoints(ring);
      if (points.length < 3) return;

      clearPendingOverlay();
      draftPointsRef.current = points;
      setDraftPointCount(points.length);
      setEditingCropId(crop.id);
      setMeasureOnly(false);
      setActiveTool(TOOLS.POLYGON);
      updateDraftPreview(TOOLS.POLYGON);
    },
    [mapReady, cropAreas, clearPendingOverlay, updateDraftPreview]
  );

  const savePendingLine = useCallback(
    async (name) => {
      if (!pendingShape || pendingShape.type !== TOOLS.LINE) return;
      setSavingDrawing(true);
      try {
        await saveFarmMapLine({
          farmId,
          name,
          pathWkt: pendingShape.pathWkt,
          lengthM: pendingShape.distanceM,
        });
        clearDraftOverlays();
        assignPendingShape(null);
        await loadDrawings();
        if (onUnpublishedChange) onUnpublishedChange();
      } finally {
        setSavingDrawing(false);
      }
    },
    [pendingShape, farmId, clearDraftOverlays, loadDrawings, onUnpublishedChange, assignPendingShape]
  );

  const savePendingCrop = useCallback(
    async (cropName) => {
      const shape = pendingShapeRef.current;
      const trimmedCropName = stripPlotAreaSuffix(
        cropName || (shape && shape.cropName) || ""
      );
      if (!shape || shape.type !== TOOLS.POLYGON || !trimmedCropName) {
        throw new Error("Informe o nome do talhão antes de salvar.");
      }
      setSavingDrawing(true);
      try {
        const maps = mapsRef.current;
        const holes = (Array.isArray(shape.holes) ? shape.holes : []).filter(
          (hole) => hole && hole.length >= 3
        );
        const validationError = validateHolesInsideOuter(shape.points, holes);
        if (validationError) {
          throw new Error(validationError);
        }

        const metrics = buildPolygonShapeMetrics(shape.points, holes, maps);
        if (holes.length > 0 && !/\),\s*\(/.test(metrics.polygonWkt)) {
          throw new Error("Não foi possível montar a geometria com recortes internos.");
        }

        const payload = {
          farmId,
          cropName: trimmedCropName,
          polygonWkt: metrics.polygonWkt,
          areaHa: metrics.areaHa,
        };

        let savedId = shape.editCropId || null;
        if (shape.editCropId) {
          await updateCropAreaGeometry(shape.editCropId, payload);
        } else {
          const saved = await saveCropArea(payload);
          savedId = saved && saved.id ? saved.id : savedId;
        }

        const refreshed = await fetchFarmCropAreas(farmId);
        const savedPlot = savedId
          ? refreshed.find((item) => String(item.id) === String(savedId))
          : refreshed
              .filter((item) => (item.crop_name || "").trim() === trimmedCropName)
              .sort((a, b) => b.id - a.id)[0];

        if (holes.length > 0) {
          const savedHoles = savedPlot
            ? parseLandplotPolygon(savedPlot).holes.length
            : 0;
          if (savedHoles < holes.length) {
            throw new Error(
              "Os recortes internos não foram gravados. Desenhe o recorte totalmente dentro do talhão e tente novamente."
            );
          }
        }

        setCropAreas(refreshed);
        clearDraftOverlays();
        assignPendingShape(null);
        setEditingCropId(null);
        if (onUnpublishedChange) onUnpublishedChange();
        return { holeCount: holes.length, savedPlot };
      } finally {
        setSavingDrawing(false);
      }
    },
    [farmId, mapsRef, clearDraftOverlays, onUnpublishedChange, assignPendingShape]
  );

  const removeCrop = useCallback(
    async (cropId) => {
      await deleteCropArea(cropId, farmId);
      setCropAreas((current) =>
        current.filter((item) => String(item.id) !== String(cropId))
      );
      await loadDrawings();
      if (onUnpublishedChange) onUnpublishedChange();
    },
    [farmId, loadDrawings, onUnpublishedChange]
  );

  const removeLine = useCallback(
    async (lineId) => {
      await deleteFarmMapLine(lineId, farmId);
      setSavedLines((current) =>
        current.filter((item) => String(item.id) !== String(lineId))
      );
      await loadDrawings();
      if (onUnpublishedChange) onUnpublishedChange();
    },
    [farmId, loadDrawings, onUnpublishedChange]
  );

  const renameCrop = useCallback(
    async (cropId, cropName) => {
      const crop = cropAreas.find((item) => item.id === cropId);
      const trimmedName = (cropName || "").trim();
      if (!crop || !trimmedName) return;

      await updateCropAreaName(cropId, {
        cropName: trimmedName,
        areaHa: crop.area_h2,
      });
      await loadDrawings();
      if (onUnpublishedChange) onUnpublishedChange();
    },
    [cropAreas, loadDrawings, onUnpublishedChange]
  );

  const savePendingFarm = useCallback(
    async (farmName) => {
      if (!pendingShape || pendingShape.type !== TOOLS.FARM) {
        return null;
      }

      const isUpdate = Boolean(pendingShape.editSubFarmId);
      const trimmedName = (
        farmName ||
        pendingShape.subFarmName ||
        ""
      ).trim();
      if (!isUpdate && !trimmedName) {
        return null;
      }

      setSavingDrawing(true);
      try {
        if (isUpdate) {
          const updated = await updateGroupFarmBoundary(
            farmId,
            pendingShape.editSubFarmId,
            pendingShape.polygonWkt
          );
          clearPendingOverlay();
          await loadDrawings();
          if (onUnpublishedChange) onUnpublishedChange();
          return updated;
        }

        const created = await createGroupFarm(
          farmId,
          trimmedName,
          pendingShape.polygonWkt,
          { alwaysCreate: true }
        );
        clearPendingOverlay();
        await loadDrawings();
        if (onUnpublishedChange) onUnpublishedChange();
        return created;
      } finally {
        setSavingDrawing(false);
      }
    },
    [pendingShape, farmId, clearPendingOverlay, loadDrawings, onUnpublishedChange]
  );

  const minDraftPoints = isAddingHole || activeTool === TOOLS.POLYGON || activeTool === TOOLS.FARM
    ? 3
    : activeTool === TOOLS.LINE
      ? 2
      : 3;

  return {
    TOOLS,
    activeTool,
    measureOnly,
    pendingShape,
    isAddingHole,
    cropAreas,
    savedLines,
    loadingDrawings,
    savingDrawing,
    draftPointCount,
    draftLineDistanceM,
    draftLastSegmentM,
    minDraftPoints,
    startTool,
    closeTool,
    clearPendingOverlay,
    clearMeasureDraft: clearDraftPoints,
    finishDraftDrawing,
    undoDraftPoint,
    startAddingHole,
    startAddingHoleToCrop,
    cancelAddingHole,
    removeHoleAt,
    savePendingLine,
    savePendingCrop,
    savePendingFarm,
    removeLine,
    removeCrop,
    renameCrop,
    reloadDrawings: loadDrawings,
    editingCropId,
    editingSubFarmId,
    startEditingCrop,
    startEditingSubFarm,
  };
}
